import { cloneDeep } from 'lodash';
import { BuilderType, projectsCacheTags } from './projectsApiHelpers';
import { Facility } from '../types/Client';
import { build } from 'vite';
import { legacyFulfill } from '../utils';
import {
  PROJECTS_RECEIVED_CREATE_PROJECT,
  PROJECTS_RECEIVED_STATS,
  PROJECTS_RECEIVED_MONITOR_PROJECTS,
  PROJECTS_RECEIVED_UPDATE_PROJECT,
  PROJECTS_RECEIVED_OPERATIONS_DETAIL,
  PROJECTS_RECEIVED_CREATE_WORKFLOW,
  PROJECTS_RECEIVED_CHECK_OPERATIONS_SUMMARY,
} from '../../legacy/actions/project-actions';
import { MonitorProject } from '../../@types/types.generated';
import { ResponseOf } from '../../@types/api-utils';

export interface Project {
  id: number;
  client_id: number;
  facility_id: number;
  name: string;
  project_data: ProjectData;
  created_at: string;
  updated_at: string;
  is_archived: boolean;
  project_workflows: ProjectWorkflow[];
  client: Client;
  facility: Facility;
}

export interface Facility {
  id: number;
  client_id: number;
  portal_location_id: null;
  facility_type: null;
  facility_size_data: FacilitySizeData;
  facility_environment_data: null;
  facility_electrical_data: null;
  facility_workforce_data: null;
  facility_infrastructure_data: null;
  created_at: string;
  updated_at: string;
  facility_contacts: any[];
  time_zone: string;
  facility_audit_data: null;
  address_id: number;
  is_archived: null;
  facility_types: string[];
  portal_location_account_id: null;
  name: string;
  description: null;
  address: Address;
}

export interface Address {
  id: number;
  client_id: number;
  portal_address_id: null;
  created_at: string;
  updated_at: string;
  address_type: null;
  country: string;
  state: string;
  city: string;
  street_1: string;
  street_2: null;
  street_3: null;
  postal_code: string;
  time_zone: string;
  latitude: null;
  longitude: null;
  name: string;
}

export interface FacilitySizeData {
  fleet_size: number;
}

export interface Client {
  id: number;
  portal_account_id: number;
  created_at: string;
  updated_at: string;
  client_type: string;
  total_fleet_size: number;
  primary_contact_id: null;
  is_archived: boolean;
  parent_id: number;
  new_client_base_id: null;
  channel_type: string;
  is_home_account: boolean;
  fy_start_month: null;
  last_updated: string;
  record_version: string;
  last_portal_update: string;
  name: string;
  account_number: null;
  description: null;
  addresses: Address[];
  client_owner: ClientOwner;
}

export interface ClientOwner {
  id: number;
  client_id: number;
  target_client_id: number;
  user_id: number;
  created_at: string;
  updated_at: string;
}

export interface Address {
  id: number;
  client_id: number;
  portal_address_id: null;
  created_at: string;
  updated_at: string;
  address_type: null;
  country: string;
  state: null | string;
  city: string;
  street_1: string;
  street_2: null | string;
  street_3: null;
  postal_code: string;
  time_zone: string;
  latitude: null;
  longitude: null;
  name: string;
}

export interface ProjectWorkflow {
  id: number;
  project_id: number;
  workflow_type: string;
  current_workflow_phase_id: null;
  date_last_activity: string;
  workflow_data: WorkFlowData;
  created_at: string;
  updated_at: string;
  lead_team_resource_id: number;
  vehicles: Vehicle[];
}

export interface Vehicle {
  id: number;
  name: string;
  vehicle_data: Vehicledata;
  created_at: string;
  updated_at: string;
  project_workflow_id: number;
  vehicle_type: string;
  vehicle_capacity: string;
  vehicle_make: string;
  vehicle_model: string;
  vehicle_voltage: string;
  annual_hours: number;
  vehicle_role: string;
  alert_configuration_id: number;
  import_id: null;
  schedule_group_id: number;
}

export interface Vehicledata {
  model_data: Modeldata;
}

export interface Modeldata {
  solution_count: number;
  electrical_cost: number;
  discount: number;
  show_lp_coo: boolean;
  hide_coo_panel: boolean;
  gnb_solutions: any[];
  competitor_solutions: any[];
  daily_energy_usage: Dailyenergyusage;
}

interface Dailyenergyusage {
  has_power_study: boolean;
  lp_cost_per_gallon: number;
  lp_coo: null;
  schedules: WorkFlowData;
}

interface WorkFlowData {
  power_study_end_date?: string;
  power_study_start_date?: string;
}

interface Verificationtasks {
  same_vehicle: boolean;
}

interface Preparationtasks {
  secure_loggers: boolean;
  contact_facility: boolean;
}

interface Pdfreportoptions {
  show_facility_audits: boolean;
  show_notes: boolean;
}

interface Loggerlist {
  id: number;
  name: string;
  status: string;
}

interface Projectdata {
  working_with_dealer?: boolean;
  dealer_id?: number;
  project_contact_ids?: number[];
  facility_contact_ids?: number[];
  some_data?: string;
}

interface ProjectStatsResponse {
  project_stats: Projectstats;
  workflow_stats: Workflowstats;
  client_stats: Clientstats;
  facility_stats: Facilitystats;
  general_stats: Generalstats;
}

interface Generalstats {
  project_creation_dates: string[];
  project_update_dates: string[];
  inactive_projects: number;
}

interface Facilitystats {
  facilities_by_type: Facilitiesbytype;
}

interface Facilitiesbytype {
  [key: string]: number;
  warehouse: number;
  distribution_center: number;
  manufacturing: number;
  cold_storage: number;
  other: number;
  headquarters: number;
}

interface Clientstats {
  total_clients_by_type: Totalclientsbytype;
  average_fleet_size_by_client: string;
  top_active_clients: Topactiveclients;
}

interface Topactiveclients {
  [key: string]: number;
}

interface Totalclientsbytype {
  dealer: number;
  location: number;
  org: number;
  root: number;
  home: number;
  client: number;
}

interface Workflowstats {
  last_activity_by_workflow: Lastactivitybyworkflow;
}

interface Lastactivitybyworkflow {
  [key: string]: string | null;
}

interface Projectstats {
  total_projects_by_client: Totalprojectsbyclient;
  total_workflows_by_type: Totalworkflowsbytype;
  current_workflow_phases: Currentworkflowphases;
  active_vs_archived_projects: Activevsarchivedprojects;
  project_activity_dates: Projectactivitydates;
}

interface Projectactivitydates {
  created_at: string[];
  updated_at: string[];
}

interface Activevsarchivedprojects {
  active: number;
  archived: number;
}

interface Currentworkflowphases {
  '1': number;
  '5': number;
  '8': number;
  '': number;
}

interface Totalworkflowsbytype {
  '': number;
  model: number;
  monitor: number;
  measure: number;
}

interface Totalprojectsbyclient {
  [key: string]: number;
}

export type PaginatedApiResponse<T> = {
  results: T[];
  meta: MetaData;
};
export type MetaData = {
  currentPage: number;
  perPage: number;
  totalEntries: number;
  totalPages: number;
};

export interface MonitorProjectSummary {
  id: number;
  project_id: number;
  name: string;
  is_archived: boolean;
  client_id: number;
  client_name: string;
  facility_id: number;
  facility_name: string;
  workflow_type: string;
  date_last_activity: string;
  date_created_at: string;
  fleet_id: number;
  alert_configuration_id: number;
  job_id: string;
  app_id: string;
  assets: MonitorProjectSummaryAsset[];
}

export interface MonitorProjectSummaryAsset {
  id: number;
  asset_type: string;
  pl_serial_number?: string;
  asset_serial: string;
  service_entry_date: null;
}

export const projectTags = [
  projectsCacheTags.PROJECTS,
  projectsCacheTags.FLEETS,
  projectsCacheTags.FLEET_ASSET_TEMPLATES,
  projectsCacheTags.FACILITIES,
  projectsCacheTags.VEHICLES,
];

export const serviceProjectsApi = (builder: BuilderType) => ({
  createProject: builder.mutation<any, Partial<Project> & { deferCacheInvalidation?: boolean }>({
    query: ({ deferCacheInvalidation, ...project }) => ({
      url: `service/projects`,
      method: 'POST',
      body: fixupProjectData(project),
    }),
    // Combined project/workflow saves refresh after both writes finish.
    invalidatesTags: (_result, _error, { deferCacheInvalidation }) =>
      deferCacheInvalidation ? [] : projectTags,
    onQueryStarted: legacyFulfill(PROJECTS_RECEIVED_CREATE_PROJECT),
  }),

  getProjects: builder.query<{ result: PaginatedApiResponse<Project> }, void>({
    query: () => 'service/projects',
    providesTags: projectTags,
  }),

  getProjectsStats: builder.query<ProjectStatsResponse, void>({
    query: () => 'service/projects/stats',
    providesTags: [projectsCacheTags.PROJECTS],
    onQueryStarted: legacyFulfill(PROJECTS_RECEIVED_STATS),
  }),

  getProject: builder.query<ResponseOf<'/service/projects/{id}'>, number | string>({
    query: (id) => `service/projects/${+id}`,
    providesTags: (result) => [
      { type: projectsCacheTags.PROJECTS, id: result?.id },
      ...projectTags,
    ],
  }),

  searchProjects: builder.query<
    PaginatedApiResponse<Project>,
    {
      pagination?: { page: number; perPage: number } | boolean;
      search: { [key: string]: any };
      sort: { [key: string]: any };
    }
  >({
    query: ({ pagination = {}, search = {}, sort = {}, ...params }) => {
      const paramString = new URLSearchParams(params ?? {}).toString();
      // prettier-ignore
      const searchString = "search="+ Object.keys(search).map((k) => `${k}=${search[k]}`).join(',');
      // prettier-ignore
      const sortString = "sort="+ Object.keys(sort).map((k) => `${k}=${sort[k]}`).join(',');

      const paginationParams = !pagination
        ? 'pagination=false'
        : new URLSearchParams(pagination).toString();
      return `service/projects/search?${paramString}&${searchString}&${sortString}&${paginationParams}`;
    },
  }),

  getProjectsWithGeoPosition: builder.query<Project[], void>({
    query: () => 'service/projects/with-geo-position',
    providesTags: [projectsCacheTags.PROJECTS],
    onQueryStarted: legacyFulfill(PROJECTS_RECEIVED_MONITOR_PROJECTS),
  }),

  updateProject: builder.mutation<any, Partial<Project>>({
    query: (project) => ({
      url: `service/projects/${project.id}`,
      method: 'PUT',
      body: fixupProjectData(project),
    }),
    invalidatesTags: [projectsCacheTags.PROJECTS],
    onQueryStarted: legacyFulfill(PROJECTS_RECEIVED_UPDATE_PROJECT),
  }),

  // prettier-ignore
  getOperationsDetail: builder.query<any, { projectId: number; vehicleId: number; date: string }>({
    query: ({ projectId, vehicleId, date }) =>
      `service/projects/${projectId}/vehicles/${vehicleId}/power_logger_detail/${date}.json`,
    providesTags: [projectsCacheTags.PROJECTS],
    onQueryStarted: legacyFulfill(PROJECTS_RECEIVED_OPERATIONS_DETAIL),
  }),
  // prettier-ignore
  createWorkflow: builder.mutation< any, { projectId: number; workflowType: string; workflowData: any }>({
    query: ({ projectId, workflowType, workflowData }) => ({
      url: `service/projects/${projectId}/workflow.json`,
      method: "POST",
      body: fixupWorkflowData(workflowType, workflowData),
    }),
    invalidatesTags: [projectsCacheTags.PROJECTS],
    onQueryStarted: legacyFulfill(
      PROJECTS_RECEIVED_CREATE_WORKFLOW
    ),
  }),

  // prettier-ignore
  checkOperationsSummary: builder.query<any,{ projectId: number; vehicleId: number }>({
    query: ({ projectId, vehicleId }) =>
      `service/projects/${projectId}/vehicles/${vehicleId}/has_summary_data`,
    providesTags: [projectsCacheTags.PROJECTS],
    onQueryStarted: legacyFulfill(PROJECTS_RECEIVED_CHECK_OPERATIONS_SUMMARY),
  }),

  getProjectNumberByClientIds: builder.query<
    { [clientId: number]: number },
    { clientIds: number[] }
  >({
    query: ({ clientIds }) => ({
      url: `service/projects/total-by-clients?ids=${clientIds.join(',')}`,
    }),
    providesTags: [projectsCacheTags.PROJECTS],
  }),
  getMonitorProjectsSummary: builder.query<MonitorProjectSummary[], void>({
    query: () => 'service/projects/monitor-summary',
    providesTags: [projectsCacheTags.PROJECTS],
  }),
  getMeasureModelSummary: builder.query<ResponseOf<'/service/projects/measure-model'>, void>({
    query: () => 'service/projects/measure-model-summary',
    providesTags: [projectsCacheTags.PROJECTS],
  }),
});

export function fixupProjectData(project) {
  const copy = cloneDeep(project);
  if (copy.is_archived === undefined) copy.is_archived = false;

  copy.project_data = JSON.stringify(project.project_data);
  copy.project_workflows_attributes = copy.project_workflows;
  delete copy.project_workflows;
  copy.project_workflows_attributes.forEach((workflow) => {
    workflow.workflow_data = JSON.stringify(workflow.workflow_data);
  });

  return copy;
}

export function fixupWorkflowData(workflowType, workflowData) {
  let workflow_data_string = JSON.stringify(workflowData);

  return {
    workflow_data_string: workflow_data_string,
    workflow_type: workflowType,
  };
}
